<template>
  <div class="overview-container">
    <div class="header-container">
      <div>
        <h1 v-bg-typography="'h1'">Summary</h1>
      </div>
      <div v-if="testData" class="stats-container">
        <div class="stat">
          <div class="stat-title">Participants</div>
          <div class="stat-answer">{{ testState.participants.length - 1 }}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Male / Female</div>
          <div class="stat-answer">{{ male }} / {{ female }}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Average age</div>
          <div class="stat-answer">{{ age }}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Target group</div>
          <div class="stat-answer capitalized">
            {{ testState.target_group }}
            <bg-tooltip
              :dark="true"
              class="bg-field__icon-container"
              v-if="testState.target_group === 'custom'"
            >
              <template slot="content">{{ testState.custom_target }}</template>
              <template slot="target">
                <bg-icon class="bg-field__icon" name="information" />
              </template>
            </bg-tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="container" v-if="testData !== null">
      <bg-video-player
        :video="testData.video"
        :getRef="setVideoRef"
        :currentTime="currentTime"
        :timeUpdate="timeUpdate"
        :newScrubberTime="newScrubberTime"
        :updateMetaData="handleVideoLoad"
      />
      <div class="chart-container" v-if="videoRef && testData">
        <bg-emotion-chart
          class="emotion-chart"
          :height="130"
          :data="testData"
          :onGraphClick="setNewTime"
        />
        <bg-emotion-chart-labels
          v-if="testData && duration"
          :data="testData"
          :duration="duration"
          :currentTime="currentTime"
        />
        <bg-scrubber
          :key="scrubberKey"
          :scrubberTimeChange="setNewTime"
          v-if="videoRef"
          :currentTime="currentTime"
          :videoRef="videoRef"
          mouseDownTrigger
        />
        <bg-section
          :data="testData"
          :currentTime="currentTime"
          class="section"
        />
        <bg-legend class="legend" />
      </div>
    </div>
    <div v-if="testData">
      <h2 v-bg-typography="'h2'">Consultant summary</h2>
      <div v-for="section in testData.sections" :key="section.id">
        <div
          v-for="(note, index) in section.consultancy_notes"
          :key="`note_${index}`"
        >
          <div
            v-if="note.note_type === 'summary'"
            class="consultancy-notes"
            :class="{ active: activeSectionId == section.id }"
          >
            <h3 :class="['consultancy-notes__title']" v-bg-typography="'h3'">
              {{ section.label.name }}
            </h3>
            <div class="consultancy-notes__duration">
              {{ timeDiff(videoStartedAt, section.timestamp_start) }} -
              {{ timeDiff(videoStartedAt, section.timestamp_end) }}
            </div>
            <div class="consultancy-notes__note" v-html="note.note"></div>
            <div class="consultancy-notes__details-link">
              <router-link
                :to="`/video-test/${$route.params.id}/scenes?scene_details=${section.id}`"
              >
                {{ `${section.label.name} details` }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$green: var(--bg-color-green);
$pink: var(--bg-color-pink);
$yellow: var(--bg-color-yellow);
.header-container {
  display: flex;
  max-width: 800px;
}

.legend {
  flex-direction: row-reverse;
  padding-bottom: 20px;
}

.chart-container {
  width: 100%;
  height: 160px;
  position: relative;
  margin-top: 15px;
  margin-bottom: 90px;
}

.emotion-chart {
  margin-bottom: 25px;
}

.overview-container,
.container {
  max-width: 800px;
  width: 100%;
}

.video-container {
  position: relative;
}

.stats-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  width: 100%;
  justify-content: flex-end;
}

.stat {
  margin-right: 50px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.stat-question {
  font-weight: 300;
}

.stat-answer {
  font-weight: 700;
  font-size: 24px;
  color: var(--bg-color-bg-dark);
}

.nav,
.navButtons {
  display: flex;
}

.nav {
  max-width: 70%;
}

.navButton {
  margin-left: 7px;
}

.consultancy-notes {
  padding: 20px;
  background-color: white;
  border-radius: var(--bg-border-radius);
  margin-bottom: 20px;
}

.consultancy-notes__title {
  margin-bottom: 10px;
}

.consultancy-notes__duration {
  margin-bottom: 10px;
}

.consultancy-notes__note {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 33px;
  letter-spacing: 0.5px;
  padding: 20px 0px;
}
.consultancy-notes__details-link {
  text-decoration: underline;
  margin-top: 15px;
  & > a {
    color: black;
  }
}

.capitalized {
  text-transform: capitalize;
}

.active {
  border: solid 2px;
}
</style>

<script>
import moment from "moment";
import BgSectionVideo from "@/components/bg-section-video";
import BgEmotionChart from "@/components/bg-video-chart/bg-emotion-chart";
import BgSection from "@/components/bg-video-chart/bg-section";
import BgVideoPlayer from "@/components/bg-video-chart/bg-video-player";
import BgNavigation from "@/components/bg-navigation";
import BgEmotionChartLabels from "@/components/bg-video-chart/bg-emotion-chart/bg-emotion-chart-labels";
import BgScrubber from "@/components/bg-video-chart/bg-scrubber";
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BgLegend from "@/components/bg-legend";
import BgIcon from "@/components/bg-icon";
import BgTooltip from "@/components/bg-tooltip";
import API from "@/api";
import { mapState } from "vuex";
export default {
  name: "overview",
  components: {
    BgSectionVideo,
    BgEmotionChart,
    BgVideoPlayer,
    BgLegend,
    BgScrubber,
    BgEmotionChartLabels,
    BgSection,
    BgSelect,
    BgNavigation,
    BgIcon,
    BgTooltip,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      testData: null,
      videoRef: null,
      totalTime: 0,
      currentTime: 0,
      newScrubberTime: 0,
      scrubberKey: 2,
      options: [],
      selectedScene: null,
      duration: null,
      videoStartedAt: null,
      activeSectionId: null,
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
    }),
    male() {
      let males = 0;
      this.testState.participants
        .filter((p) => p.type === "default")
        .forEach((p) => {
          if (p.demographic.gender === "male") {
            males = males + 1;
          }
        });
      return males;
    },
    female() {
      let females = 0;
      this.testState.participants
        .filter((p) => p.type === "default")
        .forEach((p) => {
          if (p.demographic.gender === "female") {
            females = females + 1;
          }
        });
      return females;
    },
    age() {
      return (
        this.testState.participants
          .filter((p) => p.type === "default")
          .reduce((sum, p) => {
            return sum + p.demographic.age;
          }, 0) /
          this.testState.participants.length -
        1
      ).toFixed();
    },
  },
  watch: {
    "$route.params.id": function (id) {
      this.fetchData();
    },
  },
  methods: {
    timeDiff(timeFirst, timeSecond) {
      const momentObj = moment(timeSecond, "x");
      const difference = momentObj.diff(timeFirst, "milliseconds");
      const momentDiff = moment.duration(difference);
      const time = moment()
        .seconds(momentDiff.seconds())
        .minutes(momentDiff.minutes());

      return time.format("mm:ss");
    },
    fetchData() {
      const fakeId = this.testState.participants.find(
        (p) => p.type === "video_heatmap"
      ).id;
      const testId = this.$route.params.id;
      API.test.getFakeParticipant(testId, fakeId).then((res) => {
        const scenes = res.data.sections.map((s) => {
          return {
            label: s.label.name,
            value: s.id,
          };
        });
        this.videoStartedAt = res.data.started_at;

        this.options = scenes;
        this.selectedScene = scenes[0];

        this.testData = {
          ...res.data,
          significant_points: [],
        };
      });
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    timeUpdate(time) {
      this.checkForSection(time);
      this.currentTime = time;
    },
    checkForSection(newTime) {
      const currentTime = moment(this.testData.started_at + newTime * 1000);
      const activeSection = this.testData.sections.find((s) => {
        return currentTime.isBetween(s.timestamp_start, s.timestamp_end);
      });
      if (activeSection) {
        this.selectedScene = this.options.find((o) => {
          return activeSection.label.name === o.label;
        });
        this.activeSectionId = activeSection.id;
      } else {
        this.activeSectionId = 0;
      }
      return activeSection;
    },
    setNewTime(newTimePercentage) {
      this.newScrubberTime = this.videoRef.duration * newTimePercentage;
    },
    handleVideoLoad(video) {
      this.duration = video.duration;
      if (video) {
        if (this.$route.query.timestamp) {
          const startTime = moment(testState.started_at);
          const endTime = moment(testState.ended_at);
          const startPos = moment(Number(this.$route.query.timestamp));
          const videoLength = endTime.diff(startTime, "seconds");
          const timeElapsed = startPos.diff(startTime, "seconds");
          this.setNewTime(timeElapsed / videoLength);
        }
        this.totalTime = video.duration;
      }
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
